/**
 * Shared formatters and posture-verb logic for the Posture page.
 *
 * All currency values flow through the project's Decimal money discipline
 * upstream; here we only render the already-typed numbers.
 */

import type { AutonomyReadiness } from '../../types/system'

export interface PostureVerb {
  word: string
  /** Tailwind class for color — text-amber, text-risk, text-capital, or text-muted */
  tone: string
  semantic: 'ready' | 'gated' | 'halted' | 'stopped'
}

export function postureVerb(readiness: AutonomyReadiness): PostureVerb {
  if (
    !readiness.tradingModeKnown ||
    readiness.tradingMode === 'halted' ||
    !readiness.tradingModeAllowsAction
  ) {
    return { word: 'Halted.', tone: 'text-risk', semantic: 'halted' }
  }
  if (!readiness.brokerGateOk || !readiness.riskGovernorAllowsTrading) {
    return { word: 'Halted.', tone: 'text-risk', semantic: 'halted' }
  }
  const latestPaperDecisionIsActionable =
    readiness.tradingMode === 'paper' &&
    readiness.latestAction === 'approve_for_execution' &&
    readiness.profileAutonomyEnabled &&
    readiness.marketSessionOpen &&
    readiness.tradingModeAllowsAction &&
    readiness.decisionContextFresh &&
    readiness.cashFresh &&
    readiness.cashAvailable &&
    readiness.executionEngineReady !== false
  if (latestPaperDecisionIsActionable) {
    return { word: 'Ready.', tone: 'text-capital', semantic: 'ready' }
  }
  if (
    !readiness.dataFresh ||
    !readiness.cashFresh ||
    !readiness.cashAvailable ||
    !readiness.decisionContextFresh
  ) {
    return { word: 'Gated.', tone: 'text-amber', semantic: 'gated' }
  }
  if (!readiness.profileAutonomyEnabled) {
    return { word: 'Stopped.', tone: 'text-muted', semantic: 'stopped' }
  }
  if (readiness.readyToAct) {
    return { word: 'Ready.', tone: 'text-capital', semantic: 'ready' }
  }
  return { word: 'Gated.', tone: 'text-amber', semantic: 'gated' }
}

export function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

export function formatPercent(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(fractionDigits)}%`
}

export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds} seconds`
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60)
    return m === 1 ? '1 minute' : `${m} minutes`
  }
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

export function formatLabel(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ') : 'none'
}

export function plural(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}

export function asNumber(value: number | string | null | undefined, fallback = 0): number {
  if (value === null || value === undefined) return fallback
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}
